import { createContext, useContext, type ReactNode } from 'react';

/**
 * Cross-cutting "is this workspace read-only" flag (ENG-1071). Threaded
 * through the real MeshGen workspace (`MeshGenWorkspace`, `LeftSidebar`,
 * `MeshAuthoringSection`, the mutation hooks it drives) so the public
 * `/share/geometry/$uuid` viewer can reuse the exact same component tree the
 * authed `/meshgen/$uuid` route renders, instead of forking a parallel
 * read-only copy.
 *
 * Defaults to `false` via plain `createContext(false)` — a tree with no
 * `WorkspaceReadOnlyProvider` above it behaves exactly as before. Two routes
 * mount one: the share route with `value={true}`, and `/_app/meshgen/$uuid`
 * in study mode with `isStudyMeshingReadOnly(study)`, so a study's meshes open
 * view-only once it is past meshing or for a reader who cannot edit it.
 *
 * Deliberately NOT a throw-if-missing context (unlike `useMeshAuthoring` /
 * `useStudyContext`): callers are store-driven components and mutation hooks
 * that already render/run fine with no provider mounted, and a missing
 * provider must silently mean "not read-only", never a crash.
 */
const WorkspaceReadOnlyContext = createContext(false);

export function WorkspaceReadOnlyProvider({
  value,
  children,
}: {
  value: boolean;
  children: ReactNode;
}) {
  return (
    <WorkspaceReadOnlyContext.Provider value={value}>
      {children}
    </WorkspaceReadOnlyContext.Provider>
  );
}

/**
 * Returns `true` only inside a `<WorkspaceReadOnlyProvider value={true}>` —
 * `false` everywhere else (including with no provider mounted at all).
 */
export function useWorkspaceReadOnly(): boolean {
  return useContext(WorkspaceReadOnlyContext);
}
